// Says which of the person's own picks moved or disappeared when the published
// set times changed, and links to each. Dismissing it clears the list.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LINEUP_CHANGES_KEY, useApp } from '../../data/store.tsx';
import type { LineupChange } from '../../engine/lineup-changes.ts';
import { fmtTime } from '../../engine/time.ts';
import '../../styles/planning-refinements.css';

export function LineupChangeNotice() {
  const { state, derived, storage } = useApp();
  const [changes, setChanges] = useState<LineupChange[]>([]);

  useEffect(() => {
    let dropped = false;
    void storage.meta<LineupChange[]>(LINEUP_CHANGES_KEY).then((held) => {
      if (!dropped) setChanges(Array.isArray(held) ? held : []);
    });
    return () => {
      dropped = true;
    };
  }, [storage]);

  if (changes.length === 0) return null;

  const dayLabel = (dayId: string) => state.days.find((d) => d.id === dayId)?.label ?? '';
  const stageName = (stageId: string) => derived.stageById.get(stageId)?.name ?? '';
  const where = (s: LineupChange['before']) =>
    `${dayLabel(s.dayId)} ${fmtTime(s.startTs)}, ${stageName(s.stageId)}`;

  return (
    <aside className="notice lineup-changes" aria-label="Set time changes">
      <strong>
        {changes.length} of your sets changed {changes.length === 1 ? 'time' : 'times'}
      </strong>
      <ul>
        {changes.map((change) => (
          <li key={`${change.kind}:${change.setId}`}>
            <Link to={`/plan?day=${encodeURIComponent((change.after ?? change.before).dayId)}`}>
              {change.artistName}
            </Link>
            {change.after
              ? `: was ${where(change.before)}, now ${where(change.after)}.`
              : `: ${where(change.before)} is no longer on the bill.`}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="small ghost"
        onClick={() => {
          setChanges([]);
          void storage.setMeta(LINEUP_CHANGES_KEY, []);
        }}
      >
        Dismiss
      </button>
    </aside>
  );
}
